export const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

export const readTimestamp = (row = {}, snakeKey) => readField(row, snakeKey, null);

const normalizeCandidateTimeSlot = (slot = {}) => ({
  ...slot,
  startsAt: readField(slot, "starts_at", null),
  selected: readField(slot, "selected", false) === true,
});

export const normalizeContractCandidate = (candidate = {}) => ({
  ...candidate,
  displayName: readField(candidate, "display_name", ""),
  hourlyCents: Number(readField(candidate, "hourly_cents", 0) || 0),
  matchScore: Number(readField(candidate, "match_score", 0) || 0),
  profitabilityScore: Number(readField(candidate, "profitability_score", 0) || 0),
  matchFeedback: readField(candidate, "match_feedback", ""),
  matchStrengths: Array.isArray(readField(candidate, "match_strengths", []))
    ? readField(candidate, "match_strengths", [])
    : [],
  matchGaps: Array.isArray(readField(candidate, "match_gaps", []))
    ? readField(candidate, "match_gaps", [])
    : [],
  matchGeneratedAt: readField(candidate, "match_generated_at", null),
  matchModel: readField(candidate, "match_model", ""),
  updatedAt: readField(candidate, "updated_at", null),
  contractorDecision: readField(candidate, "contractor_decision", "pending"),
  contractorDecisionAt: readField(candidate, "contractor_decision_at", null),
  timeSlots: Array.isArray(readField(candidate, "time_slots", []))
    ? readField(candidate, "time_slots", []).map(normalizeCandidateTimeSlot)
    : [],
  interviewStatus: readField(candidate, "interview_status", "not_scheduled"),
  interviewAt: readField(candidate, "interview_at", null),
  interviewOutcomeAt: readField(candidate, "interview_outcome_at", null),
  interviewOutcomeBy: readField(candidate, "interview_outcome_by", ""),
  interviewRequestedAt: readField(candidate, "interview_requested_at", null),
  interviewRequestedBy: readField(candidate, "interview_requested_by", ""),
  adminFeedback: readField(candidate, "admin_feedback", ""),
  clientFeedback: readField(candidate, "client_feedback", ""),
  contractorFeedback: readField(candidate, "contractor_feedback", ""),
  selectedAt: readField(candidate, "selected_at", null),
  selectedBy: readField(candidate, "selected_by", ""),
});

export const normalizeContractNestedContractor = (contractor = null) => {
  if (!contractor || typeof contractor !== "object") return contractor || null;
  return {
    ...contractor,
    displayName: readField(contractor, "display_name", ""),
    hourlyCents: Number(readField(contractor, "hourly_cents", 0) || 0),
  };
};

export const normalizeContractBilling = (billing = null) => {
  if (!billing || typeof billing !== "object") return billing || null;
  return {
    ...billing,
    productId: readField(billing, "product_id", ""),
    activePriceId: readField(billing, "active_price_id", ""),
    unitAmountCents: Number(readField(billing, "unit_amount_cents", 0) || 0),
    monthlyAmountCents: Number(readField(billing, "monthly_amount_cents", 0) || 0),
    effectiveFrom: readField(billing, "effective_from", null),
    updatedAt: readField(billing, "updated_at", null),
    initializedAt: readField(billing, "initialized_at", null),
    priceInitLock: readField(billing, "price_init_lock", ""),
    priceInitLockedAt: readField(billing, "price_init_locked_at", null),
    lastInitError: readField(billing, "last_init_error", ""),
  };
};

export const normalizeContractPricingSnapshot = (snapshot = null) => {
  if (!snapshot || typeof snapshot !== "object") return snapshot || null;
  return {
    ...snapshot,
    pricingModel: readField(snapshot, "pricing_model", ""),
    hourlyBudgetCents: Number(readField(snapshot, "hourly_budget_cents", 0) || 0),
    monthlyBudgetCents: Number(readField(snapshot, "monthly_budget_cents", 0) || 0),
    contractorUid: readField(snapshot, "contractor_uid", ""),
    contractor: normalizeContractNestedContractor(snapshot.contractor || null),
    lockedAt: readField(snapshot, "locked_at", null),
  };
};

export const normalizeContractStatusHistoryItem = (item = {}) => ({
  ...item,
  candidateCount: readField(item, "candidate_count", undefined),
  candidateUid: readField(item, "candidate_uid", undefined),
});

export const normalizeContractDoc = (id, data = {}) => {
  const accountId = readField(data, "account_id", "");
  const accountName = readField(data, "account_name", "");
  const companyName = readField(data, "company_name", "");
  const clientUid = readField(data, "client_uid", "");
  const assignedAdminUid = readField(data, "assigned_admin_uid", "");
  const contractorUid = readField(data, "contractor_uid", "");
  const matchedContractorUid = readField(data, "matched_contractor_uid", "");
  const candidateContractors = readField(data, "candidate_contractors", []);
  const candidateUids = readField(data, "candidate_uids", []);
  const statusHistory = readField(data, "status_history", []);
  const createdAt = readTimestamp(data, "created_at");
  const updatedAt = readTimestamp(data, "updated_at");
  const contractStartAt = readTimestamp(data, "contract_start_at");
  const contractEndAt = readTimestamp(data, "contract_end_at");
  const startDate = readTimestamp(data, "start_date");
  const desiredStartDate = readTimestamp(data, "desired_start_date");
  const endDate = readTimestamp(data, "end_date");

  return {
    id,
    ...data,
    accountId,
    accountName,
    assignedAdminUid,
    clientUid,
    companyName,
    contractorUid,
    matchedContractorUid,
    billing: normalizeContractBilling(data.billing || null),
    contractor: normalizeContractNestedContractor(data.contractor || null),
    pricingSnapshot: normalizeContractPricingSnapshot(readField(data, "pricing_snapshot", null)),
    candidateContractors: Array.isArray(candidateContractors) ? candidateContractors.map(normalizeContractCandidate) : [],
    candidateUids: Array.isArray(candidateUids) ? candidateUids : [],
    statusHistory: Array.isArray(statusHistory) ? statusHistory.map(normalizeContractStatusHistoryItem) : [],
    contractStartAt,
    contractEndAt,
    startDate,
    desiredStartDate,
    endDate,
    aiNormalizedRequirements: readField(data, "ai_normalized_requirements", null),
    aiNormalization: readField(data, "ai_normalization", null),
    aiMatching: readField(data, "ai_matching", null),
    createdAt,
    updatedAt,
    roleCode: readField(data, "role_code", ""),
    roleLabel: readField(data, "role_label", ""),
    roleTitle: readField(data, "role_title", ""),
    roleLevel: readField(data, "role_level", ""),
    roleLevelLabel: readField(data, "role_level_label", ""),
    roleFamily: readField(data, "role_family", ""),
    roleSpecialty: readField(data, "role_specialty", ""),
    roleSeniority: readField(data, "role_seniority", ""),
    requirementsHtml: readField(data, "requirements_html", ""),
    requirementsFormat: readField(data, "requirements_format", "plain"),
    projectTermMonths: readField(data, "project_term_months", null),
    closedReason: readField(data, "closed_reason", ""),
    contractApproval: readField(data, "contract_approval", null),
    contractExecution: readField(data, "contract_execution", null),
    pricingModel: readField(data, "pricing_model", ""),
    suggestedHourlyCents: readField(data, "suggested_hourly_cents", 0),
    suggestedMonthlyCents: readField(data, "suggested_monthly_cents", 0),
    hourlyBudgetCents: readField(data, "hourly_budget_cents", 0),
    monthlyBudgetCents: readField(data, "monthly_budget_cents", 0),
  };
};
